import ErrorFeedback from 'Components/ErrorFeedback'
import ModalConfirmLeave from 'Components/ModalConfirmLeave'
import PageContainer from 'Components/PageContainer'
import useRouteLeaveGuard from 'Hooks/useRouteLeaveGuard/useRouteLeaveGuard'
import useWindowLeaveGuard from 'Hooks/useWindowLeaveGuard/useWindowLeaveGuard'
import { notifyParent } from 'mailup-react-helpers'
import {
  Loading,
  SPACING_24,
  SpacingVertical,
  ToastContainer,
  Tooltip,
  TooltipUtils
} from 'mailup-web-components'
import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil'
import Header from './Components/Header'
import { ModalConcurrentAccess } from './Components/ModalConcurrentAccess/ModalConcurrentAccess'
import { ModalThirdParty } from './Components/ModalThirdParty'
import ModalNewSource from './Components/step1/ModalNewSource'
import { ModalBeeSavedRow } from './Components/step2/ModalBeeSavedRow/ModalBeeSavedRow'
import ModalContentDefaults from './Components/step2/ModalContentDefaults/ModalContentDefaults'
import { Step2 } from './Components/step2/Step2'
import WizardContainer from './Components/WizardContainer'
import {
  getMessageStructureCallback,
  getTemplateByIdCallback,
  initCallback,
  resetStateCallback
} from './recoil/callbacks/callbacks'
import { initTemplateCarouselCallback } from './recoil/callbacks/carouselCallbacks'
import { beePluginState, editorState } from './recoil/state/bee'
import { campaignState } from './recoil/state/campaign'
import { pageState, stepState, wizardSteps } from './recoil/state/page'
import { templatesState } from './recoil/state/templates'

const Campaign = () => {
  const { idCampaign } = useParams()

  const {
    loading,
    error,
    severity,
    loaded,
    isFullScreen,
    forceTemplates,
    isEnd
  } = useRecoilValue(pageState)
  const setPageState = useSetRecoilState(pageState)
  const init = useRecoilCallback(initCallback, [])
  const resetState = useRecoilCallback(resetStateCallback, [])
  const templateCallback = useRecoilCallback(initTemplateCarouselCallback, [])
  const campaign = useRecoilValue(campaignState)
  const [{ wizardStep }, setWizardStep] = useRecoilState(wizardSteps)
  const { loading: templateLoading } = useRecoilValue(templatesState)
  const getTemplateById = useRecoilCallback(getTemplateByIdCallback, [])
  const getMessageStructure = useRecoilCallback(getMessageStructureCallback, [])
  const { loadedInstance: beeLoadedInstance, loading: beeLoading } =
    useRecoilValue(beePluginState)
  const {
    step3: {
      senderInfo: { listInfoDetailsLoading }
    }
  } = useRecoilValue(stepState)
  const { step } = useParams()

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [])

  useEffect(() => {
    if (!loaded) {
      init(parseInt(idCampaign)).then((campaignData) => {
        if (!campaignData) {
          return
        }
        if (+step && +step > 0) {
          setWizardStep((prev) => ({
            ...prev,
            wizardStep: +step,
            wizardStepsDone: +step - 1
          }))
        } else {
          let wizardStep = 0
          if (
            campaignData.Status === 1 &&
            campaignData.CheckFrequencyType !== 0
          ) {
            wizardStep = 4
          } else if (
            (campaignData.Status === 1 &&
              campaignData.CheckFrequencyType === 0) ||
            campaignData.EmailOptions.IdMessage > 0
          ) {
            wizardStep = 3
          } else if (campaignData.IdSource > 0) {
            wizardStep = 2
          } else if (campaignData.IdSource == null) {
            wizardStep = 1
          }
          setWizardStep((prev) => ({
            ...prev,
            wizardStep,
            wizardStepsDone: wizardStep - 1
          }))
        }
      })
    }
  }, [loaded])

  useEffect(() => {
    return () => {
      resetState()
    }
  }, [])

  useEffect(() => {
    if (wizardStep === 2) {
      setPageState((prev) => {
        notifyParent('setFullScreen', [true])
        return { ...prev, isFullScreen: true }
      })
    } else {
      setPageState((prev) => {
        notifyParent('setFullScreen', [false])
        return { ...prev, isFullScreen: false }
      })
    }

    return () => {
      if (wizardStep !== 2) {
        setPageState((prev) => {
          notifyParent('setFullScreen', [false])
          return { ...prev, isFullScreen: false }
        })
      }
    }
  }, [wizardStep])

  useEffect(() => {
    if (isFullScreen) {
      if (forceTemplates) {
        // passaggio forzato da step 4 a 'cambia template'
        setPageState((prev) => ({
          ...prev,
          displayCarousel: true,
          step2SelectTemplate: true
        }))

        getTemplateById()
      } else {
        if (
          campaign.EmailOptions.IdTemplate &&
          campaign.EmailOptions.IdTemplate > 0
        ) {
          setPageState((prev) => ({
            ...prev,
            step2SelectTemplate: false
          }))

          if (
            campaign.EmailOptions.IdMessage &&
            campaign.EmailOptions.IdMessage > 0
          ) {
            // template selezionato e messaggio salvato
            getMessageStructure()
          } else {
            // template selezionato ma messaggio mai salvata
            getTemplateById()
          }
        }
      }
      templateCallback(!!campaign.EmailOptions.TemplateType)
    }
  }, [isFullScreen])

  // Avviso l'utente se sta abbandonando la pagina con dati non salvati
  const { saved, dirty } = useRecoilValue(editorState)

  const history = useHistory()
  let condition
  if (wizardStep === 2) {
    condition = !dirty || saved
  } else {
    if (wizardStep === 4 && isEnd) {
      condition = true
      history.push('/automation/contentsCampaign/list')
    } else {
      condition = !campaign.isDirty
    }
  }
  const [prompt, allow, deny] = useRouteLeaveGuard(condition)
  useWindowLeaveGuard(condition)

  return (
    <>
      <Tooltip />
      <Loading
        open={
          loading ||
          templateLoading ||
          (!beeLoadedInstance && beeLoading) ||
          listInfoDetailsLoading
        }
      />
      <ToastContainer />
      <ModalThirdParty />
      <ModalConcurrentAccess />
      <ModalNewSource />
      <ModalContentDefaults />
      <ModalBeeSavedRow />
      <ModalConfirmLeave show={prompt} onConfirm={allow} onCancel={deny} />
      {loaded && (
        <>
          {!isFullScreen && (
            <>
              <Header />
              <PageContainer className='c-workflow'>
                {error && (
                  <>
                    <ErrorFeedback
                      error={error}
                      severity={severity}
                      onClickReload={() =>
                        setPageState((prev) => ({
                          ...prev,
                          loaded: false
                        }))
                      }
                    />
                    <SpacingVertical variant={SPACING_24} />
                  </>
                )}
                {!error && <WizardContainer />}
              </PageContainer>
            </>
          )}
          {isFullScreen && <Step2 />}
        </>
      )}
    </>
  )
}

export default Campaign
